import { Trans, useLingui } from '@lingui/react/macro'
import { ActionIcon, Center, Loader, Menu, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { AppPage } from '@markku/components'
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react'
import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import {
  maintenanceLogHooks,
  maintenanceTaskHooks,
} from '../../../lib/trpcHooks'
import { SystemTaskDetailsModal } from '../../../modules/maintenance/components/SystemTaskDetailsModal'
import { TaskDetailsModal } from '../../../modules/maintenance/components/TaskDetailsModal'
import { MaintenanceLogDetail } from '../../../modules/maintenanceLog/detail'

function MaintenanceLogDetailPage() {
  const { t } = useLingui()
  const { id } = Route.useParams()
  const logId = Number(id)
  const navigate = useNavigate()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false)

  const { data: log, isLoading: logLoading } =
    maintenanceLogHooks.useGetById(logId)
  const { data: task, isLoading: taskLoading } =
    maintenanceTaskHooks.useGetById(log?.maintenanceTaskId ?? 0, {
      enabled: Boolean(log?.maintenanceTaskId),
    })
  const { mutateAsync: deleteLog } = maintenanceLogHooks.useDelete()
  const { mutateAsync: deleteTask } = maintenanceTaskHooks.useDelete()

  const isLoading = logLoading || taskLoading

  // Check if this is a system task (propertyId = NULL)
  const isSystemTask = task?.propertyId === null

  const handleEdit = () => {
    openEditModal()
  }

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t`Delete entry`,
      centered: true,
      children: (
        <Text>
          <Trans>Are you sure you want to delete this maintenance entry?</Trans>
        </Text>
      ),
      labels: { confirm: t`Delete`, cancel: t`Cancel` },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        if (log) {
          // For system tasks, only delete the log entry
          // For property tasks, delete both log and task
          if (isSystemTask) {
            await deleteLog({ id: logId })
          } else {
            await Promise.all([
              deleteLog({ id: logId }),
              ...(log.maintenanceTaskId
                ? [deleteTask({ id: log.maintenanceTaskId })]
                : []),
            ])
          }

          // Use browser back to preserve navigation context (search params, etc.)
          if (canGoBack) {
            router.history.back()
          } else {
            navigate({ to: '/maintenance' })
          }
        }
      },
    })
  }

  if (isLoading) {
    return (
      <AppPage title={t`Maintenance task`} backTarget="/maintenance">
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </AppPage>
    )
  }

  if (!log) {
    return (
      <AppPage title={t`Task not found`} backTarget="/maintenance">
        <Center py="xl">
          <Trans>Maintenance task not found</Trans>
        </Center>
      </AppPage>
    )
  }

  // Action menu for user-created logs
  const actionMenu = (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <IconDotsVertical size={20} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconEdit size={16} />} onClick={handleEdit}>
          <Trans>Edit</Trans>
        </Menu.Item>
        <Menu.Item
          leftSection={<IconTrash size={16} />}
          color="red"
          onClick={handleDelete}
        >
          <Trans>Delete</Trans>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )

  return (
    <>
      <AppPage
        title={log.taskNameSnapshot}
        subtitle={t`Completed maintenance task`}
        backTarget="/maintenance"
        rightSection={actionMenu}
      >
        <MaintenanceLogDetail log={log} />
      </AppPage>

      {/* Edit Modal - Use SystemTaskDetailsModal for system tasks, TaskDetailsModal for property tasks */}
      {editModalOpened &&
        (isSystemTask && task ? (
          <SystemTaskDetailsModal
            onClose={closeEditModal}
            task={{
              id: task.id.toString(),
              title: task.name,
              description: task.description || undefined,
              category: task.category || undefined,
              estimatedDuration: task.estimatedDurationMinutes
                ? t`About ${task.estimatedDurationMinutes} minutes`
                : undefined,
              isUserTask: true, // Show editable form (not Markku package message)
              completed: !!log,
              log: log,
              attachments: log?.attachments,
            }}
            propertyId={log.propertyId} // Use log's propertyId, not task's (which is NULL)
            hasPackage={false} // Not relevant for this context
            onSave={() => {
              closeEditModal()
            }}
          />
        ) : (
          <TaskDetailsModal
            task={task}
            log={log}
            propertyId={log.propertyId}
            opened={editModalOpened}
            onClose={closeEditModal}
            onDeleted={() => {
              // Navigate away since item no longer exists
              if (canGoBack) {
                router.history.back()
              } else {
                navigate({ to: '/maintenance' })
              }
            }}
          />
        ))}
    </>
  )
}

export const Route = createFileRoute('/_app/maintenance-log/$id')({
  component: MaintenanceLogDetailPage,
})
